import { usePage } from "@inertiajs/react";
import {
	AppWindow,
	Calendar1,
	CalendarCheck,
	FlaskConical,
	Hospital,
	LayoutDashboard,
	Users,
} from "lucide-react";
import { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { humanize, populateQueryParams } from "@/lib/utils";
import type { AdminTypes } from "@/types/admin-portal/admin";
import type { GlobalPageProps } from "@/types/globals";
import type { NavMainProps } from "../admin-portal/sidebar/nav-main";
import type { NavUserProps } from "../admin-portal/sidebar/nav-user";

type NavigationProviderProps = {
	children: React.ReactNode;
};

type NaviagtionProviderState = {
	navigation: {
		main: NavMainProps | null;
		user: NavUserProps | null;
	};
};

// Utility to strip query parameters
const stripQueryParams = (url: string) => url.split("?")[0];
// Compare URLs ignoring query parameters
const isActiveLink = (
	currentUrl: string,
	menuUrl: string,
	rootPath: string,
) => {
	const cleanCurrentUrl = stripQueryParams(currentUrl);
	const cleanMenuUrl = stripQueryParams(menuUrl);
	const isRoot = cleanMenuUrl === rootPath;

	const exactOrSubMatch =
		cleanCurrentUrl === cleanMenuUrl ||
		cleanCurrentUrl.startsWith(`${cleanMenuUrl}/`);

	return isRoot
		? exactOrSubMatch
		: exactOrSubMatch || cleanCurrentUrl.includes(cleanMenuUrl);
};

const SHOW_LABS_MENU = false;

const initialState: NaviagtionProviderState = {
	navigation: {
		main: null,
		user: null,
	},
};

const NavigationProviderContext =
	createContext<NaviagtionProviderState>(initialState);

export function NavigationProvider({
	children,
	...props
}: NavigationProviderProps) {
	const { props: globalProps, url: currentUrl } = usePage<GlobalPageProps>();
	const { t } = useTranslation("side-menu");

	const navUserProps = useMemo<NavUserProps>(() => {
		const currentUser = globalProps.auth.currentUser;
		const globalRouter = globalProps.adminPortal.router;

		const userData = {
			name: humanize(currentUser?.name || "Admin"),
			email: currentUser?.user.email || "",
			avatar: "",
			type: (currentUser?.adminType ?? "ADMIN") as AdminTypes[number],
		};
		const url = {
			logout: globalRouter.logout,
			account: globalRouter.adminPortal.settings.accountSecurity,
		};

		return { userData, url };
	}, [globalProps]);

	const navMainProps = useMemo<NavMainProps>(() => {
		const { adminPortal, authenticatedRootPath } =
			globalProps.adminPortal.router;

		// * Helper to create menu items
		const createMenuItem = (
			title: string,
			url: string,
			icon: any,
			subItems: { title: string; url: string; isActive: boolean }[] = [],
		) => ({
			title,
			url,
			icon,
			isActive: false,
			subItems,
		});

		const buildDashboardMenu = () =>
			createMenuItem("Dashboard", authenticatedRootPath, LayoutDashboard);

		const buildAppointmentMenu = () => {
			const { fullUrl: allAppointmentsUrl } = populateQueryParams(
				adminPortal.appointment.index,
			);

			const baseSubItems = [
				{
					title: t("all_appointments"),
					url: allAppointmentsUrl,
					isActive: false,
				},
				{
					title: t("pending_therapist_assignment"),
					url: adminPortal.appointment.unassignedVisits,
					isActive: false,
				},
				{
					title: t("pending_review_mitra"),
					url: adminPortal.appointment.pendingReviews,
					isActive: false,
				},
				{
					title: t("order_ownership"),
					url: adminPortal.orderOwnership.index,
					isActive: false,
				},
			];

			return createMenuItem(
				t("appointment"),
				allAppointmentsUrl,
				Calendar1,
				baseSubItems,
			);
		};

		const buildBookingsMenu = () => {
			const subItems = [
				{
					title: t("therapist_schedules"),
					url: adminPortal.therapistManagement.schedules,
					isActive: false,
				},
				{
					title: t("availability_settings"),
					url: adminPortal.availability.index,
					isActive: false,
				},
			];

			return createMenuItem(
				t("bookings"),
				adminPortal.therapistManagement.schedules,
				CalendarCheck,
				subItems,
			);
		};

		const buildUserManagementMenu = () => {
			const baseSubItems = [
				{
					title: t("admins"),
					url: adminPortal.adminManagement.index,
					isActive: false,
				},
				{
					title: t("therapists"),
					url: adminPortal.therapistManagement.index,
					isActive: false,
				},
				{
					title: t("patients"),
					url: adminPortal.patientManagement.index,
					isActive: false,
				},
				{
					title: t("users"),
					url: adminPortal.userManagement.index,
					isActive: false,
				},
			];

			return createMenuItem(
				t("user_management"),
				adminPortal.adminManagement.index,
				Users,
				baseSubItems,
			);
		};

		const buildServiceManagementMenu = () => {
			const subItems = [
				{
					title: t("brands"),
					url: adminPortal.serviceManagement.index,
					isActive: false,
				},
				{
					title: t("locations"),
					url: adminPortal.locationManagement.index,
					isActive: false,
				},
			];

			subItems.push({
				title: t("vouchers"),
				url: adminPortal.vouchers.index,
				isActive: false,
			});

			// Only present when User#bonus_ro_access? lets the admin in.
			if (adminPortal.bonusRo?.index) {
				subItems.push({
					title: t("bonus_ro"),
					url: adminPortal.bonusRo.index,
					isActive: false,
				});
			}

			return createMenuItem(
				t("brand_and_location"),
				adminPortal.serviceManagement.index,
				Hospital,
				subItems,
			);
		};

		const isSuperAdmin = globalProps.auth.currentUser?.["isSuperAdmin?"];

		const buildAppsMenu = () => {
			const subItems: {
				title: string;
				url: string;
				isActive: boolean;
			}[] = [];
			const telegramBroadcastEnabled =
				!!globalProps.adminPortal.featureFlags?.telegramBroadcastsEnabled;

			if (isSuperAdmin) {
				subItems.push({
					title: t("feature_flags"),
					url: adminPortal.featureFlags.index,
					isActive: false,
				});
			}

			if (adminPortal.routingMode?.index) {
				subItems.push({
					title: t("routing_mode"),
					url: adminPortal.routingMode.index,
					isActive: false,
				});
			}

			if (isSuperAdmin && adminPortal.genericContents?.index) {
				subItems.push({
					title: t("generic_contents"),
					url: adminPortal.genericContents.index,
					isActive: false,
				});
			}

			if (isSuperAdmin && adminPortal.serviceCredentials?.index) {
				subItems.push({
					title: t("service_credentials"),
					url: adminPortal.serviceCredentials.index,
					isActive: false,
				});
			}

			if (adminPortal.mitraBlastConfigs?.index) {
				subItems.push({
					title: t("mitra_blast_configs"),
					url: adminPortal.mitraBlastConfigs.index,
					isActive: false,
				});
			}

			if (telegramBroadcastEnabled) {
				subItems.push({
					title: t("telegram_broadcasts"),
					url: adminPortal.telegramBroadcasts.index,
					isActive: false,
				});
			}

			if (!subItems.length) return null;

			return createMenuItem(t("apps"), subItems[0].url, AppWindow, subItems);
		};

		const buildLabsMenu = () => {
			if (!SHOW_LABS_MENU) return null;

			const subItems: {
				title: string;
				url: string;
				isActive: boolean;
			}[] = [];

			if (adminPortal.chat?.index) {
				subItems.push({
					title: t("ai_chat"),
					url: adminPortal.chat.index,
					isActive: false,
				});
			}

			if (adminPortal.therapistDiagnosis?.index) {
				subItems.push({
					title: t("therapist_diagnosis"),
					url: adminPortal.therapistDiagnosis.index,
					isActive: false,
				});
			}

			if (!subItems.length) return null;

			return createMenuItem(t("labs"), subItems[0].url, FlaskConical, subItems);
		};

		const appsMenu = buildAppsMenu();
		const labsMenu = buildLabsMenu();

		const menus = [
			buildDashboardMenu(),
			buildAppointmentMenu(),
			buildBookingsMenu(),
			buildUserManagementMenu(),
			buildServiceManagementMenu(),
			...(appsMenu ? [appsMenu] : []),
			...(labsMenu ? [labsMenu] : []),
		];

		// * Determine which items are active
		const finalItems = menus.map((menu) => {
			const updatedMenu = { ...menu, isActive: false };

			if (menu.subItems.length) {
				updatedMenu.subItems = menu.subItems.map((subItem) => {
					const active = isActiveLink(
						currentUrl,
						subItem.url,
						authenticatedRootPath,
					);
					if (active) updatedMenu.isActive = true;
					return { ...subItem, isActive: active };
				});
			} else {
				updatedMenu.isActive = isActiveLink(
					currentUrl,
					menu.url,
					authenticatedRootPath,
				);
			}

			return updatedMenu;
		});

		return { items: finalItems };
	}, [currentUrl, globalProps, t]);

	return (
		<NavigationProviderContext.Provider
			{...props}
			value={{ navigation: { main: navMainProps, user: navUserProps } }}
		>
			{children}
		</NavigationProviderContext.Provider>
	);
}

export const useNavigation = () => {
	const context = useContext(NavigationProviderContext);

	if (context === undefined)
		throw new Error(
			"useNavigation must be used within a NavigationProviderContext",
		);

	return context;
};
